import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';

import { notifications } from '../../api/notifications';
import { useT } from '../../i18n/provider';

/**
 * The bell in the shell header — a link to `/notifications` wearing the
 * unread count.
 *
 * It polls every 30 s rather than holding a Mercure subscription open on
 * every screen: the badge may be half a minute stale, which is fine for a
 * count, and the one EventSource-per-view budget of `13 §5` stays with the
 * views that show actual content. `limit: 1` because only `meta.unread_count`
 * is wanted — the rows would be thrown away.
 *
 * La cuenta entra y sale con muelle, y el punto late mientras haya algo sin
 * leer: es la única cosa de la cabecera que cambia sola, y sin movimiento pasa
 * desapercibida. `prefers-reduced-motion` lo apaga desde `<MotionConfig>`.
 */
export function NotificationsBell() {
  const t = useT();

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notifications.list({ read: false, limit: 1 }),
    refetchInterval: 30_000,
  });

  const count = unread.data?.meta.unread_count ?? 0;

  return (
    <Link
      to="/notifications"
      className="relative rounded-md p-1.5 text-ink-soft transition-colors hover:bg-hover hover:text-ink"
      aria-label={count > 0 ? t('notifications.bellUnread', { count: String(count) }) : t('notifications.bell')}
    >
      <Bell className="size-5" aria-hidden="true" />
      <AnimatePresence>
        {count > 0 ? (
          <m.span
            key="count"
            aria-hidden="true"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 520, damping: 24 }}
            className="animate-radar absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-primary-ink"
          >
            {count > 9 ? '9+' : count}
          </m.span>
        ) : null}
      </AnimatePresence>
    </Link>
  );
}
